import { SnackbarMessageVariants } from '../../../../helpers/enums';
import {
    TCXUser, TCXUserCreationResponse, TCXSearchParam
} from '../../../../interfaces/TCXInterfaces';
import { enqueueSnackbar } from '../../notificationActions';
import ApiService from '../../../../services/apiService';
import Endpoints from '../../../../services/endpoints';

export const ADMIN_CLEAR_VALID_STATE = 'ADMIN_CLEAR_VALID_STATE';
export const ADMIN_SET_USER_DATA = 'ADMIN_SET_USER_DATA';
export const ADMIN_SET_IS_EDITABLE = 'ADMIN_SET_IS_EDITABLE';
export const ADMIN_SET_EMAIL_VALID_STATE = 'ADMIN_SET_EMAIL_VALID_STATE';
export const ADMIN_SET_FNAME_VALID_STATE = 'ADMIN_SET_FNAME_VALID_STATE';
export const ADMIN_SET_LNAME_VALID_STATE = 'ADMIN_SET_LNAME_VALID_STATE';

export const RECEIVE_ADMIN_GET_ALL_USERS = 'RECEIVE_ADMIN_GET_ALL_USERS';
export const REQUEST_ADMIN_GET_ALL_USERS_FAILED = 'REQUEST_ADMIN_GET_ALL_USERS_FAILED';
export const REQUEST_ADMIN_GET_ALL_USERS = 'REQUEST_ADMIN_GET_ALL_USERS';

export const RECEIVE_ADMIN_SAVE_USER = 'RECEIVE_ADMIN_SAVE_USER';
export const REQUEST_ADMIN_SAVE_USER_FAILED = 'REQUEST_ADMIN_SAVE_USER_FAILED';
export const REQUEST_ADMIN_SAVE_USER = 'REQUEST_ADMIN_SAVE_USER';

export const REQUEST_ADMIN_DELETE_USER = 'REQUEST_ADMIN_DELETE_USER';
export const REQUEST_ADMIN_DELETE_USER_FAILED = 'REQUEST_ADMIN_DELETE_USER_FAILED';
export const RECEIVE_ADMIN_DELETE_USER = 'RECEIVE_ADMIN_DELETE_USER';

export const ADMIN_SET_SEARCH_PARAMS = 'ADMIN_SET_SEARCH_PARAMS';

export const clearValidState = (): object => {
    return {
        type: ADMIN_CLEAR_VALID_STATE
    };
};

export const setUserData = (userData: TCXUser): object => {
    return {
        type: ADMIN_SET_USER_DATA,
        payload: userData
    };
};

export const setIsEditable = (isEditable: boolean): object => {
    return {
        type: ADMIN_SET_IS_EDITABLE,
        payload: isEditable
    };
};

const isEmptyString = (value?: string): boolean => {
    return !value || value === '';
};

export const validateEmail = (email: string): object => {
    // Updated regex to properly handle all TLDs including .us (2+ character TLDs)
    // Pattern validates: local-part@domain.tld where:
    // - local-part: alphanumeric, dots, hyphens, underscores, plus signs
    // - domain: alphanumeric, dots, hyphens
    // - TLD: 2 or more letters (supports .us, .com, .co.uk, etc.)
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (isEmptyString(email)) {
        return {
            type: ADMIN_SET_EMAIL_VALID_STATE,
            payload: 'Email Id is Required'
        };
    }

    if (email && !emailRegex.test(email)) {
        return {
            type: ADMIN_SET_EMAIL_VALID_STATE,
            payload: 'Invalid Email'
        };
    }

    return {
        type: ADMIN_SET_EMAIL_VALID_STATE,
        payload: undefined
    };
};

export const validateFirstName = (firstName: string): object => {
    const validRegex = new RegExp("^[A-Za-z,.'-]?[A-Za-z,. '-]*$");
    const rangeRegex = new RegExp('^.{2,40}$');

    if (isEmptyString(firstName)) {
        return {
            type: ADMIN_SET_FNAME_VALID_STATE,
            payload: 'First Name is required'
        };
    }

    if (firstName && !validRegex.test(firstName)) {
        return {
            type: ADMIN_SET_FNAME_VALID_STATE,
            payload: 'Few sp chars are now allowed in name'
        };
    }

    if (firstName && !rangeRegex.test(firstName)) {
        return {
            type: ADMIN_SET_FNAME_VALID_STATE,
            payload: 'Name should be between 2-40 chars'
        };
    }

    return {
        type: ADMIN_SET_FNAME_VALID_STATE,
        payload: undefined
    };
};

export const validateLastName = (lastName: string): object => {
    const validRegex = new RegExp("^[A-Za-z,.'-]?[A-Za-z,. '-]*$");
    const rangeRegex = new RegExp('^.{2,40}$');

    if (isEmptyString(lastName)) {
        return {
            type: ADMIN_SET_LNAME_VALID_STATE,
            payload: 'Last Name is required'
        };
    }

    if (lastName && !validRegex.test(lastName)) {
        return {
            type: ADMIN_SET_LNAME_VALID_STATE,
            payload: 'Few sp chars are now allowed in name'
        };
    }

    if (lastName && !rangeRegex.test(lastName)) {
        return {
            type: ADMIN_SET_LNAME_VALID_STATE,
            payload: 'Name should be between 2-40 chars'
        };
    }

    return {
        type: ADMIN_SET_LNAME_VALID_STATE,
        payload: undefined
    };
};

export const validateForm = (): object => {
    return (dispatch: Function, getState: Function): void => {
        const { userData } = getState().manageUsers;

        dispatch(clearValidState());
        dispatch(validateEmail(userData.email));
        dispatch(validateFirstName(userData.firstName));
        dispatch(validateLastName(userData.lastName));
    };
};

const requestAllUsers = (): object => {
    return {
        type: REQUEST_ADMIN_GET_ALL_USERS
    };
};

const requestAllUsersFailed = (): object => {
    return {
        type: REQUEST_ADMIN_GET_ALL_USERS_FAILED
    };
};

const receiveAllUsers = (users: TCXUser[]): object => {
    return {
        type: RECEIVE_ADMIN_GET_ALL_USERS,
        payload: users
    };
};

export const setSearchParam = (param: TCXSearchParam): object => {
    return {
        type: ADMIN_SET_SEARCH_PARAMS,
        payload: param
    };
};

export const fetchAllUsers = (params?: TCXSearchParam): object => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        let tcxSearchParam = params;
        if (tcxSearchParam) {
            dispatch(setSearchParam(tcxSearchParam));
        } else {
            const { searchParam } = getState().manageUsers;
            tcxSearchParam = { ...searchParam };
        }

        let uri;
        if (tcxSearchParam?.value) {
            uri = `${Endpoints.getTCXUsers}?query=${encodeURIComponent(tcxSearchParam?.value)}`;
        } else {
            uri = Endpoints.getTCXUsers;
        }
        dispatch(requestAllUsers());

        try {
            const users: TCXUser[]|string = await ApiService.get(uri, true);
            let result = users;
            if (result === '') {
                result = [];
            }

            dispatch(receiveAllUsers(result as TCXUser[]));
        } catch (err) {
            dispatch(requestAllUsersFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while fetching users.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};

const requestSaveUser = (): object => {
    return {
        type: REQUEST_ADMIN_SAVE_USER
    };
};

const requestSaveUserFailed = (): object => {
    return {
        type: REQUEST_ADMIN_SAVE_USER_FAILED
    };
};

const receiveSaveUser = (): object => {
    return {
        type: RECEIVE_ADMIN_SAVE_USER
    };
};

export const addUser = (): object => {
    return async (dispatch: Function, getState: Function): Promise<void> => {

        const {
            userData, isFormValid
        } = getState().manageUsers;

        dispatch(validateForm());

        if (isFormValid) {
            dispatch(requestSaveUser());

            let uri;
            try {
                uri = `${Endpoints.createTCXUsers}?sendEmail=true`;
                const jsonData: TCXUserCreationResponse[] = await ApiService.post(uri, [userData], true);
                if (jsonData && jsonData[0].responseStatus === 'OK') {
                    dispatch(receiveSaveUser());
                    dispatch(enqueueSnackbar({
                        message: `User with email address ${userData.email} was created.`,
                        variant: SnackbarMessageVariants.Success
                    }));
                } else {
                    dispatch(requestSaveUserFailed());
                    dispatch(enqueueSnackbar({
                        message: `Error occured ${jsonData[0].message || ''}`,
                        variant: SnackbarMessageVariants.Error
                    }));
                }
            } catch (err) {
                dispatch(requestSaveUserFailed());
                dispatch(enqueueSnackbar({
                    message: 'Error occurred while adding user.',
                    variant: SnackbarMessageVariants.Error
                }));
            }
        }
    };
};

export const updateUser = (): object => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        dispatch(validateForm());

        const { userData, isFormValid } = getState().manageUsers;

        if (isFormValid) {
            dispatch(requestSaveUser());

            try {
                await ApiService.put(`${Endpoints.updateTCXUsers}`, userData, true);
                dispatch(receiveSaveUser());
                dispatch(enqueueSnackbar({
                    message: `User with email, ${userData.email} is updated.`,
                    variant: SnackbarMessageVariants.Success
                }));
            } catch (err) {
                dispatch(requestSaveUserFailed());
                dispatch(enqueueSnackbar({
                    message: 'Error occurred while updating the user.',
                    variant: SnackbarMessageVariants.Error
                }));
            }
        }
    };
};

const requestDeleteUser = (): object => {
    return {
        type: REQUEST_ADMIN_DELETE_USER
    };
};

const requestDeleteUserFailed = (): object => {
    return {
        type: REQUEST_ADMIN_DELETE_USER_FAILED
    };
};

const receiveDeleteUser = (): object => {
    return {
        type: RECEIVE_ADMIN_DELETE_USER
    };
};

export const deleteUser = (userData: TCXUser): object => {
    return async (dispatch: Function): Promise<void> => {
        dispatch(requestDeleteUser());

        if (userData.email === undefined) {
            throw new Error('Missing Email');
        }
        try {
            await ApiService.delete(`${Endpoints.deleteTCXUser}/${encodeURIComponent(userData.email)}`, true);
            dispatch(enqueueSnackbar({
                message: `User with email, ${userData.email} is deleted.`,
                variant: SnackbarMessageVariants.Success
            }));
            dispatch(receiveDeleteUser());
        } catch (err) {
            dispatch(requestDeleteUserFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while deleting the user.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};
